import { DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

/** Matches the vendor sync panels, so the two do not interleave oddly. */
const POLL_MS = 3000;
/**
 * After a start, how long "no lock yet" still means "starting" rather than
 * "finished": the server takes the lock a moment after it answers.
 */
const START_GRACE_MS = 30_000;

/** The shared sync-lock row, same shape the vendor syncs report. */
interface ActiveLock {
  vendorName: string;
  collection: string;
  host: string;
  isThisInstance: boolean;
  startedAt: string;
  cancelRequested: boolean;
  done: number;
  total: number;
  currentTitle: string;
}

/**
 * Manual rebuild for the Shop's curated sections.
 *
 * The rebuild runs weekly on its own (Sunday 04:00); this is for the cases a cron
 * cannot cover — seeding a freshly provisioned database, redoing a bad AI pass, or
 * confirming the feature works without waiting for the weekend.
 *
 * Two buttons rather than one because the two passes have very different costs:
 * the rule pass is free and takes about a minute, while the full rebuild calls
 * DeepSeek for every livestock listing and takes roughly 25 minutes. Making that
 * an explicit choice keeps an expensive run from being a surprise.
 */
@Component({
  selector: "fh-shop-sections-panel",
  templateUrl: "./shop-sections-panel.component.html",
  styleUrls: ["./shop-sections-panel.component.scss"],
  standalone: true,
  imports: [DatePipe, MatButtonModule, MatIconModule, MatProgressBarModule, MatTooltipModule],
})
export class ShopSectionsPanelComponent implements OnInit, OnDestroy {
  active = signal<ActiveLock | null>(null);
  busy = signal<boolean>(false);
  message = signal<string>("");
  error = signal<string>("");

  private poll: ReturnType<typeof setInterval> | null = null;
  /** When a rebuild was started from here, until its lock is first seen; 0 otherwise. */
  private startedAt = 0;

  constructor(private requestService: RequestService) {}

  ngOnInit() {
    this.refresh();
    // The timer ticks continuously, but it only calls the API while something is
    // actually running — the same shape the vendor sync panels use.
    //
    // This matters because every API call raises the global loading indicator
    // (LoaderInterceptor -> appStateService.apiLoading$). An unconditional poll
    // therefore flashes the whole page every few seconds forever, which is
    // exactly what it did before this guard. Idle now means one call on load and
    // then silence, until a rebuild is started from here.
    this.poll = setInterval(() => {
      if (this.active() || this.busy()) this.refresh();
    }, POLL_MS);
  }

  ngOnDestroy() {
    if (this.poll) clearInterval(this.poll);
  }

  private refresh() {
    this.requestService.get<IApiResponse>("/api/v2/product-sections/status").subscribe({
      next: (res) => {
        const lock = (res.data as { active: ActiveLock | null } | undefined)?.active ?? null;
        this.active.set(lock);
        if (lock) {
          this.startedAt = 0;
          return;
        }
        // Just started and the lock is not up yet: stay busy, or both buttons
        // come back mid-start and a second rebuild can be sent.
        if (this.startedAt && Date.now() - this.startedAt < START_GRACE_MS) return;
        // A run that has finished since the last poll: clear the busy state so
        // the buttons come back without a page refresh.
        this.startedAt = 0;
        this.busy.set(false);
      },
      error: () => {
        /* A failed poll is not worth surfacing; the next one will tell. */
      },
    });
  }

  start(skipAi: boolean) {
    this.busy.set(true);
    // Set now, not on the reply: a poll landing while the request is in flight would
    // otherwise see no lock yet and clear busy.
    this.startedAt = Date.now();
    this.message.set("");
    this.error.set("");
    this.requestService
      .post<IApiResponse>("/api/v2/product-sections/rebuild", { skipAi })
      .subscribe({
        next: (res) => {
          if (!res.status) {
            this.startedAt = 0;
            this.busy.set(false);
            this.error.set(res.message || "Could not start the rebuild");
            return;
          }
          this.message.set((res.data as { note?: string } | undefined)?.note ?? "Rebuild started.");
          this.startedAt = Date.now();
          this.refresh();
        },
        error: () => {
          this.startedAt = 0;
          this.busy.set(false);
          this.error.set("Could not start the rebuild");
        },
      });
  }

  cancel() {
    this.requestService
      .post<IApiResponse>("/api/v2/product-sections/rebuild/cancel", {})
      .subscribe({
        next: () => {
          this.message.set("Cancellation requested — it stops at the next section.");
          this.refresh();
        },
        error: () => this.error.set("Could not cancel"),
      });
  }

  /** 0–100 for the progress bar. */
  progress(): number {
    const lock = this.active();
    if (!lock || !lock.total) return 0;
    return Math.round((lock.done / lock.total) * 100);
  }
}
